import { Box, Button, ButtonBase, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckIcon from '@mui/icons-material/Check'
import { FC, ReactNode, useEffect, useRef } from 'react'
import { tokens } from '@/theme'

/** Laranja escurecido só para o círculo da etapa atual: número branco com contraste AA (≥ 4.5:1). */
const PRIMARIA_AA = '#B93E12'
const SUCESSO_AA = '#15803D'

export interface EtapaNavegavel {
  rotulo: string
  /** Passo indisponível (ex.: produto sem formulário) — não recebe clique nem foco. */
  desabilitada?: boolean
  /** Legenda curta sob o rótulo (estado já conhecido pela página). */
  legenda?: ReactNode
  /** Texto da legenda para leitores de tela, quando `legenda` não é texto simples. */
  legendaAcessivel?: string
  /** Mostra ✓ no círculo — só quando a página já sabe que a etapa está completa. */
  concluida?: boolean
}

interface Props {
  etapas: readonly EtapaNavegavel[]
  /** Etapa atual, 1-based (a mesma de `?etapa=`). */
  atual: number
  onIrPara: (n: number) => void
}

/**
 * Trilha de etapas clicáveis do workspace de estruturação: cada etapa é um cartão-botão
 * com borda, hover/foco visíveis e a atual destacada (`aria-current="step"`). Em telas
 * largas as 5 dividem a linha; em telas estreitas a trilha rola na horizontal dentro do
 * próprio contêiner (a página nunca rola de lado) e o "Etapa N de 5" orienta.
 */
export const NavegacaoEtapas: FC<Props> = ({ etapas, atual, onIrPara }) => {
  const trilhaRef = useRef<HTMLOListElement>(null)
  const total = etapas.length

  // Telas estreitas: mantém a etapa atual visível dentro da trilha rolável.
  useEffect(() => {
    const trilha = trilhaRef.current
    const item = trilha?.children[atual - 1] as HTMLElement | undefined
    if (!trilha || !item || trilha.scrollWidth <= trilha.clientWidth) return
    if (typeof trilha.scrollTo === 'function') {
      trilha.scrollTo({ left: Math.max(0, item.offsetLeft - 16), behavior: 'smooth' })
    }
  }, [atual])

  return (
    <Box component="nav" aria-label="Etapas da estruturação" sx={{ mb: 3, minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          columnGap: 1.5,
          rowGap: 0.25,
          mb: 1.25,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700, color: tokens.brandInk }}>
          Etapa {atual} de {total}
          <Box component="span" sx={{ fontWeight: 500, color: tokens.textSecondary }}>
            {' · '}
            {etapas[atual - 1]?.rotulo}
          </Box>
        </Typography>
        <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
          Clique em uma etapa para navegar
        </Typography>
      </Box>

      <Box
        component="ol"
        ref={trilhaRef}
        sx={{
          listStyle: 'none',
          m: 0,
          p: 0.5,
          mx: -0.5,
          display: { xs: 'flex', md: 'grid' },
          gridTemplateColumns: { md: `repeat(${total}, minmax(0, 1fr))` },
          gap: 1.5,
          overflowX: { xs: 'auto', md: 'visible' },
          scrollSnapType: { xs: 'x proximity', md: 'none' },
        }}
      >
        {etapas.map((etapa, i) => {
          const n = i + 1
          const ehAtual = n === atual
          const desabilitada = !!etapa.desabilitada
          const legendaTexto =
            etapa.legendaAcessivel ?? (typeof etapa.legenda === 'string' ? etapa.legenda : '')
          return (
            <Box
              component="li"
              key={etapa.rotulo}
              sx={{ flex: { xs: '0 0 184px', md: 'initial' }, minWidth: 0, scrollSnapAlign: 'start' }}
            >
              <ButtonBase
                focusRipple
                disabled={desabilitada}
                onClick={() => onIrPara(n)}
                aria-current={ehAtual ? 'step' : undefined}
                aria-label={`Ir para a etapa ${n}: ${etapa.rotulo}${
                  legendaTexto ? ` — ${legendaTexto}` : ''
                }`}
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: 64,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  gap: 1.25,
                  px: 1.5,
                  py: 1.25,
                  textAlign: 'left',
                  borderRadius: '10px',
                  border: '1px solid',
                  borderColor: ehAtual ? tokens.brandPrimary : tokens.divider,
                  // Borda "dupla" da atual sem mudar o tamanho do cartão (sem pulo de layout).
                  boxShadow: ehAtual ? `inset 0 0 0 1px ${tokens.brandPrimary}` : 'none',
                  bgcolor: ehAtual ? tokens.brandPrimarySoft : tokens.surfaceCard,
                  cursor: 'pointer',
                  transition: 'border-color .15s ease, background-color .15s ease',
                  '&:hover': {
                    borderColor: tokens.brandPrimary,
                    bgcolor: tokens.brandPrimarySoft,
                  },
                  '&.Mui-focusVisible': {
                    outline: `2px solid ${PRIMARIA_AA}`,
                    outlineOffset: 2,
                  },
                  '&.Mui-disabled': {
                    cursor: 'not-allowed',
                    bgcolor: tokens.surfacePage,
                    borderStyle: 'dashed',
                  },
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    flex: '0 0 auto',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                    ...(ehAtual
                      ? { bgcolor: PRIMARIA_AA, color: '#FFFFFF' }
                      : etapa.concluida
                        ? { bgcolor: SUCESSO_AA, color: '#FFFFFF' }
                        : {
                            bgcolor: tokens.surfaceCard,
                            color: tokens.brandInk,
                            border: `1.5px solid ${desabilitada ? tokens.divider : tokens.textSecondary}`,
                          }),
                  }}
                >
                  {etapa.concluida && !ehAtual ? <CheckIcon sx={{ fontSize: 16 }} /> : n}
                </Box>
                <Box sx={{ minWidth: 0, pt: 0.25 }}>
                  <Typography
                    component="span"
                    sx={{
                      display: 'block',
                      fontSize: '0.875rem',
                      lineHeight: 1.35,
                      fontWeight: ehAtual ? 700 : 600,
                      color: desabilitada ? tokens.textSecondary : tokens.brandInk,
                    }}
                  >
                    {etapa.rotulo}
                  </Typography>
                  {etapa.legenda != null && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 0.25,
                        lineHeight: 1.3,
                        color: etapa.concluida ? SUCESSO_AA : tokens.textSecondary,
                      }}
                    >
                      {etapa.legenda}
                    </Typography>
                  )}
                </Box>
              </ButtonBase>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

interface RodapeProps {
  /** Etapa de destino de cada botão; `null` desabilita (pontas da trilha). */
  anterior: number | null
  proxima: number | null
  onIrPara: (n: number) => void
}

/** Rodapé "Etapa anterior" / "Próxima etapa" do conteúdo — mesma navegação do clique na trilha. */
export const RodapeNavegacaoEtapas: FC<RodapeProps> = ({ anterior, proxima, onIrPara }) => (
  <Box
    sx={{
      display: 'flex',
      // Telas estreitas: botões empilhados em largura total, "Próxima" em cima.
      flexDirection: { xs: 'column-reverse', sm: 'row' },
      justifyContent: 'space-between',
      gap: 1.5,
      '& .MuiButton-root': { whiteSpace: 'nowrap' },
      mt: 3,
      pt: 2.5,
      borderTop: `1px solid ${tokens.divider}`,
    }}
  >
    <Button
      variant="outlined"
      color="inherit"
      startIcon={<ArrowBackIcon />}
      disabled={anterior == null}
      onClick={() => anterior != null && onIrPara(anterior)}
    >
      Etapa anterior
    </Button>
    <Button
      variant="contained"
      endIcon={<ArrowForwardIcon />}
      disabled={proxima == null}
      onClick={() => proxima != null && onIrPara(proxima)}
    >
      Próxima etapa
    </Button>
  </Box>
)

export default NavegacaoEtapas
